import React from 'react';
import { Flex, RadioGroup, Text, TextField } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { DateInput, FormField } from 'shared/components';
import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { useDebouncedCallback } from 'use-debounce';
import { useUserProfile } from '../user-profile.provider';

const CandidatePersonalInfoView = () => {
   const userProfile = useUserProfile();
   const [countryQuery, setCountryQuery] = React.useState('');

   const debouncedCities = useDebouncedCallback((q: string) => {
      userProfile.searchCities(q);
   }, 500);

   const countryItems = React.useMemo(() => {
      const countries = userProfile.userProfile?.countries ?? [];
      if (!countryQuery) {
         return countries;
      }

      const query = countryQuery.toLowerCase();
      return countries.filter((country) => country.name.toLowerCase().includes(query));
   }, [countryQuery, userProfile.userProfile?.countries]);

   return (
      <Flex direction="column" gap="4">
         <FormField label="Ім'я" error={userProfile.fromGroup.controls.first_name.error}>
            <TextField.Root
               size="3"
               color="indigo"
               value={userProfile.fromGroup.controls.first_name.value}
               onChange={(e) => userProfile.setPersonalInfoName('first_name', e.target.value)}
               onBlur={() => userProfile.fromGroup.controls.first_name.markAsTouched()}
            />
         </FormField>

         <FormField label="Прізвище" error={userProfile.fromGroup.controls.last_name.error}>
            <TextField.Root
               size="3"
               color="indigo"
               value={userProfile.fromGroup.controls.last_name.value}
               onChange={(e) => userProfile.setPersonalInfoName('last_name', e.target.value)}
               onBlur={() => userProfile.fromGroup.controls.last_name.markAsTouched()}
            />
         </FormField>

         <FormField label="По батькові" error={userProfile.fromGroup.controls.father_name.error}>
            <TextField.Root
               size="3"
               color="indigo"
               value={userProfile.fromGroup.controls.father_name.value}
               onChange={(e) => userProfile.setPersonalInfoName('father_name', e.target.value)}
               onBlur={() => userProfile.fromGroup.controls.father_name.markAsTouched()}
            />
         </FormField>

         <FormField label="Стать">
            <RadioGroup.Root
               value={userProfile.fromGroup.controls.gender.value}
               size="3"
               color="indigo"
               variant="soft"
               onValueChange={(value) =>
                  userProfile.fromGroup.controls.gender.setValue(value as 'male' | 'female' | '')
               }
            >
               <Flex gap="4">
                  <RadioGroup.Item value="female">
                     <Text>Жіноча</Text>
                  </RadioGroup.Item>
                  <RadioGroup.Item value="male">
                     <Text>Чоловіча</Text>
                  </RadioGroup.Item>
               </Flex>
            </RadioGroup.Root>
         </FormField>

         <FormField label="Дата народження">
            <DateInput
               name="birthday"
               format="dd.MM.yyyy"
               minDate="1930-01-01"
               maxDate={new Date().toISOString().split('T')[0]}
               value={userProfile.fromGroup.controls.birthday.value}
               error={userProfile.fromGroup.controls.birthday.error}
               touched={userProfile.fromGroup.controls.birthday.touched}
               handleBlur={() => {
                  userProfile.fromGroup.controls.birthday.markAsTouched();
               }}
               handleChange={(e) => {
                  userProfile.fromGroup.controls.birthday.setValue(e.target.value);
               }}
            />
         </FormField>

         <FormField label="Країна">
            <AutocompleteV2.Root
               placeholder="Оберіть країну"
               selectedKey={userProfile.fromGroup.controls.country_id.value || null}
               items={countryItems}
               getOptionValue={(country) => country.id}
               getOptionLabel={(country) => country.name}
               onSelect={(country) => {
                  userProfile.onCountrySelect(country.id);
                  userProfile.searchCities('');
               }}
               onSearch={(value) => {
                  setCountryQuery(value);
               }}
            />
         </FormField>

         <FormField label="Місто">
            <AutocompleteV2.Root
               placeholder="Оберіть місто"
               selectedKey={userProfile.fromGroup.controls.city_id.value || null}
               items={userProfile.cityOptions}
               loading={userProfile.citiesLoading}
               disabled={!userProfile.fromGroup.controls.country_id.value}
               getOptionValue={(city) => city.id}
               getOptionLabel={(city) => city.name}
               onSelect={(city) => {
                  userProfile.onCitySelect(city.id);
               }}
               onSearch={(value) => {
                  userProfile.fromGroup.controls.city_id.setValue('');
                  debouncedCities(value);
               }}
            />
         </FormField>
      </Flex>
   );
};

export const CandidatePersonalInfo = observer(CandidatePersonalInfoView);
